<template>
  <div class="template-selector-container">
    <!-- Versión Compacta Horizontal -->
    <div class="template-compact">
      <!-- Header Inline -->
      <div class="template-inline-header">
        <div class="header-badge">
          <i class="fas fa-layer-group"></i>
          <span>Plantillas</span>
        </div>

        <!-- Selector -->
        <div class="inline-select-wrapper">
          <select v-model="selectedTemplateId" class="inline-select">
            <option :value="null">Selecciona plantilla...</option>
            <option v-for="template in templates" :key="template.id" :value="template.id">
              {{ template.name }}{{ template.is_default ? ' ★' : '' }}
            </option>
          </select>
          <i class="fas fa-chevron-down"></i>
        </div>

        <!-- Botones Compactos -->
        <div class="inline-actions">
          <button
v-if="defaultTemplateExists" class="btn-compact btn-yellow" title="Usar plantilla predeterminada"
            @click="applyDefaultTemplate">
            <i class="fas fa-star"></i>
            <span>Predeterminada</span>
          </button>

          <button
class="btn-compact btn-green" :disabled="!selectedTemplateId" title="Aplicar plantilla seleccionada"
            @click="applySelectedTemplate">
            <i class="fas fa-check"></i>
            <span>Aplicar</span>
          </button>

          <button class="btn-compact btn-blue" title="Crear nueva plantilla" @click="openCreateModal">
            <i class="fas fa-plus"></i>
            <span>Nueva</span>
          </button>

          <button
class="btn-compact btn-cyan" :disabled="!hasCurrentData" title="Guardar configuración actual"
            @click="showSaveTemplateModal = true">
            <i class="fas fa-save"></i>
            <span>Guardar</span>
          </button>

          <button
class="btn-compact btn-orange" :disabled="!selectedTemplateId" title="Editar plantilla"
            @click="openEditModal">
            <i class="fas fa-edit"></i>
          </button>

          <button
class="btn-compact btn-pink" :disabled="!selectedTemplateId || isSelectedDefault"
            :title="isSelectedDefault ? 'Ya es predeterminada' : 'Marcar como predeterminada'" @click="setAsDefault">
            <i class="fas fa-star"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Guardar Plantilla Actual -->
    <base-modal
v-model="showSaveTemplateModal" title="Guardar como Plantilla" icon="fas fa-save" confirm-text="Guardar"
      confirm-icon="fas fa-save" size="large" @confirm="saveAsTemplate">
      <div class="form-group">
        <label class="form-label">Nombre de la Plantilla</label>
        <input v-model="newTemplate.name" type="text" class="form-input" placeholder="Ej: Consulta General" />
      </div>
      <div class="form-group">
        <label class="form-label">Descripción</label>
        <textarea
v-model="newTemplate.description" class="form-textarea" rows="3"
          placeholder="Descripción de la plantilla..."></textarea>
      </div>
      <div class="form-group">
        <label class="form-checkbox">
          <input v-model="newTemplate.is_default" type="checkbox" />
          <span>Marcar como predeterminada</span>
        </label>
      </div>
    </base-modal>

    <!-- Modal: Crear Plantilla Nueva -->
    <base-modal
v-model="showCreateTemplateModal" title="Crear Nueva Plantilla" icon="fas fa-plus" confirm-text="Crear"
      confirm-icon="fas fa-plus" size="large" @confirm="createEmptyTemplate">
      <div class="columns-manager-intro">
        <i class="fas fa-info-circle"></i>
        <span>Se creará una plantilla vacía con columnas básicas</span>
      </div>

      <div class="form-group">
        <label class="form-label">Nombre de la Plantilla</label>
        <input v-model="newTemplate.name" type="text" class="form-input" placeholder="Ej: Consulta Pediátrica" />
      </div>
      <div class="form-group">
        <label class="form-label">Descripción</label>
        <textarea
v-model="newTemplate.description" class="form-textarea" rows="3"
          placeholder="Descripción de la plantilla..."></textarea>
      </div>
      <div class="form-group">
        <label class="form-checkbox">
          <input v-model="newTemplate.is_default" type="checkbox" />
          <span>Marcar como predeterminada</span>
        </label>
      </div>
    </base-modal>

    <!-- Modal: Editar Plantilla -->
    <base-modal
v-model="showEditTemplateModal" title="Editar Plantilla" icon="fas fa-edit"
      confirm-text="Guardar Cambios" confirm-icon="fas fa-save" size="large" @confirm="updateTemplate">
      <div class="form-group">
        <label class="form-label">Nombre de la Plantilla</label>
        <input v-model="editTemplate.name" type="text" class="form-input" placeholder="Ej: Consulta General" />
      </div>
      <div class="form-group">
        <label class="form-label">Descripción</label>
        <textarea
v-model="editTemplate.description" class="form-textarea" rows="3"
          placeholder="Descripción de la plantilla..."></textarea>
      </div>
      <div class="form-group">
        <label class="form-checkbox">
          <input v-model="updateColumnsFromGrid" type="checkbox" />
          <span>Actualizar columnas con el grid actual</span>
        </label>
      </div>
      <div class="form-group">
        <label class="form-checkbox">
          <input v-model="editTemplate.is_default" type="checkbox" />
          <span>Marcar como predeterminada</span>
        </label>
      </div>
    </base-modal>

    <!-- Modal: Confirmar Sobrescribir -->
    <base-modal
v-model="showOverwriteModal" title="Confirmar Aplicar Plantilla" icon="fas fa-exclamation-triangle"
      confirm-text="Sí, Aplicar Plantilla" confirm-icon="fas fa-check" size="medium" modal-class="modal-confirm"
      @confirm="confirmApplyTemplate" @close="cancelApplyTemplate">
      <p class="warning-text">
        El grid actual ya tiene datos. Al aplicar esta plantilla:
      </p>
      <ul class="warning-list">
        <li><i class="fas fa-check text-success me-2"></i>Se conservarán las columnas de la plantilla</li>
        <li><i class="fas fa-times text-danger me-2"></i>Se eliminarán todos los datos actuales</li>
      </ul>
      <p class="confirm-question">
        ¿Deseas continuar?
      </p>
    </base-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api.js';
import Swal from 'sweetalert2';
import BaseModal from '@/components/BaseModal.vue';

const props = defineProps({
  patientId: {
    type: Number,
    required: true
  },
  currentGridData: {
    type: Object,
    default: () => ({ columns: [], rows: [] })
  }
});

const emit = defineEmits(['template-applied', 'template-saved']);

// Estado
const templates = ref([]);
const selectedTemplateId = ref(null);
const showSaveTemplateModal = ref(false);
const showCreateTemplateModal = ref(false);
const showEditTemplateModal = ref(false);
const showOverwriteModal = ref(false);
const pendingTemplateToApply = ref(null);
const updateColumnsFromGrid = ref(false);

const newTemplate = ref({
  name: '',
  description: '',
  is_default: false
});

const editTemplate = ref({
  id: null,
  name: '',
  description: '',
  is_default: false
});

// Computed
// eslint-disable-next-line no-unused-vars
const selectedTemplate = computed(() => {
  return templates.value.find(t => t.id === selectedTemplateId.value) || null;
});

const isSelectedDefault = computed(() => {
  const template = templates.value.find(t => t.id === selectedTemplateId.value);
  return template?.is_default || false;
});

const defaultTemplateExists = computed(() => {
  return templates.value.some(t => t.is_default);
});

const hasCurrentData = computed(() => {
  return props.currentGridData?.rows?.length > 0 || props.currentGridData?.columns?.length > 0;
});

// Funciones
const loadTemplates = async () => {
  try {
    const response = await api.get('/doctor-grid-templates');
    templates.value = response.data.data || [];

    // Auto-aplicar plantilla predeterminada si el grid está vacío
    if (!hasCurrentData.value && templates.value.length > 0) {
      const defaultTemplate = templates.value.find(t => t.is_default);
      if (defaultTemplate) {
        console.log('🎯 Auto-aplicando plantilla predeterminada');
        await applyTemplate(defaultTemplate, true);
      }
    }
  } catch (error) {
    console.error('Error al cargar plantillas:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar las plantillas'
    });
  }
};

const applySelectedTemplate = async () => {
  if (!selectedTemplateId.value) return;

  const template = templates.value.find(t => t.id === selectedTemplateId.value);
  if (!template) return;

  if (hasCurrentData.value) {
    pendingTemplateToApply.value = template;
    showOverwriteModal.value = true;
  } else {
    await applyTemplate(template);
  }
};

const applyDefaultTemplate = async () => {
  const defaultTemplate = templates.value.find(t => t.is_default);
  if (!defaultTemplate) return;

  if (hasCurrentData.value) {
    pendingTemplateToApply.value = defaultTemplate;
    showOverwriteModal.value = true;
  } else {
    await applyTemplate(defaultTemplate);
  }
};

const applyTemplate = async (template, isAutoApply = false) => {
  try {
    const response = await api.post(`/doctor-grid-templates/${template.id}/apply-to-patient/${props.patientId}`);

    if (!isAutoApply) {
      Swal.fire({
        icon: 'success',
        title: 'Plantilla Aplicada',
        text: `La plantilla "${template.name}" se aplicó correctamente`,
        timer: 2000,
        showConfirmButton: false
      });
    }

    emit('template-applied', response.data.data);
  } catch (error) {
    console.error('❌ Error al aplicar plantilla:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo aplicar la plantilla'
    });
  }
};

const confirmApplyTemplate = async () => {
  if (pendingTemplateToApply.value) {
    await applyTemplate(pendingTemplateToApply.value);
    pendingTemplateToApply.value = null;
  }
  showOverwriteModal.value = false;
};

const cancelApplyTemplate = () => {
  pendingTemplateToApply.value = null;
  showOverwriteModal.value = false;
};

const openCreateModal = () => {
  resetNewTemplate();
  showCreateTemplateModal.value = true;
};

const saveAsTemplate = async () => {
  if (!newTemplate.value.name.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Nombre Requerido',
      text: 'Por favor ingresa un nombre para la plantilla'
    });
    return;
  }

  try {
    const gridDataToSave = {
      columns: props.currentGridData.columns || []
    };

    if (!gridDataToSave.columns || gridDataToSave.columns.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin Columnas',
        text: 'No hay columnas para guardar en la plantilla.'
      });
      return;
    }

    const payload = {
      name: newTemplate.value.name.trim(),
      description: newTemplate.value.description?.trim() || '',
      is_default: newTemplate.value.is_default ? 1 : 0,
      grid_data: gridDataToSave
    };

    const response = await api.post('/doctor-grid-templates', payload);
    templates.value.push(response.data.data);

    showSaveTemplateModal.value = false;
    resetNewTemplate();

    Swal.fire({
      icon: 'success',
      title: 'Plantilla Guardada',
      text: 'La plantilla se guardó correctamente',
      timer: 2000,
      showConfirmButton: false
    });

    emit('template-saved', response.data.data);
  } catch (error) {
    console.error('❌ Error al guardar plantilla:', error);

    let errorMessage = 'No se pudo guardar la plantilla';

    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const errorList = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      errorMessage = errorList;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage
    });
  }
};

const createEmptyTemplate = async () => {
  if (!newTemplate.value.name.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Nombre Requerido',
      text: 'Por favor ingresa un nombre para la plantilla'
    });
    return;
  }

  try {
    const basicColumns = [
      { field: 'column1', headerName: 'Columna 1', width: 150, editable: true },
      { field: 'column2', headerName: 'Columna 2', width: 150, editable: true }
    ];

    const payload = {
      name: newTemplate.value.name.trim(),
      description: newTemplate.value.description?.trim() || '',
      is_default: newTemplate.value.is_default ? 1 : 0,
      grid_data: {
        columns: basicColumns
      }
    };

    const response = await api.post('/doctor-grid-templates', payload);
    templates.value.push(response.data.data);

    showCreateTemplateModal.value = false;
    resetNewTemplate();

    Swal.fire({
      icon: 'success',
      title: 'Plantilla Creada',
      text: 'La plantilla se creó correctamente',
      timer: 2000,
      showConfirmButton: false
    });

    emit('template-saved', response.data.data);
  } catch (error) {
    console.error('❌ Error al crear plantilla:', error);

    let errorMessage = 'No se pudo crear la plantilla';

    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const errorList = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      errorMessage = errorList;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage
    });
  }
};

const openEditModal = () => {
  const template = templates.value.find(t => t.id === selectedTemplateId.value);
  if (!template) return;

  editTemplate.value = {
    id: template.id,
    name: template.name,
    description: template.description || '',
    is_default: template.is_default
  };

  updateColumnsFromGrid.value = false;
  showEditTemplateModal.value = true;
};

const updateTemplate = async () => {
  if (!editTemplate.value.name.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Nombre Requerido',
      text: 'Por favor ingresa un nombre para la plantilla'
    });
    return;
  }

  try {
    const payload = {
      name: editTemplate.value.name.trim(),
      description: editTemplate.value.description?.trim() || '',
      is_default: editTemplate.value.is_default ? 1 : 0
    };

    if (updateColumnsFromGrid.value) {
      const gridDataToSave = {
        columns: props.currentGridData.columns || []
      };

      if (!gridDataToSave.columns || gridDataToSave.columns.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin Columnas',
          text: 'No hay columnas para actualizar en la plantilla.'
        });
        return;
      }

      payload.grid_data = gridDataToSave;
    }
    const response = await api.put(`/doctor-grid-templates/${editTemplate.value.id}`, payload);

    const index = templates.value.findIndex(t => t.id === editTemplate.value.id);
    if (index !== -1) {
      templates.value[index] = response.data.data;
    }

    showEditTemplateModal.value = false;

    Swal.fire({
      icon: 'success',
      title: 'Plantilla Actualizada',
      text: 'Los cambios se guardaron correctamente',
      timer: 2000,
      showConfirmButton: false
    });

    emit('template-saved', response.data.data);
  } catch (error) {
    console.error('❌ Error al actualizar plantilla:', error);

    let errorMessage = 'No se pudo actualizar la plantilla';

    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const errorList = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      errorMessage = errorList;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage
    });
  }
};

const setAsDefault = async () => {
  if (!selectedTemplateId.value) return;

  try {
    await api.put(`/doctor-grid-templates/${selectedTemplateId.value}`, {
      is_default: 1
    });

    templates.value = templates.value.map(t => ({
      ...t,
      is_default: t.id === selectedTemplateId.value
    }));

    Swal.fire({
      icon: 'success',
      title: 'Plantilla Predeterminada',
      text: 'La plantilla se marcó como predeterminada',
      timer: 2000,
      showConfirmButton: false
    });
  } catch (error) {
    console.error('Error al marcar como predeterminada:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo actualizar la plantilla'
    });
  }
};

const resetNewTemplate = () => {
  newTemplate.value = {
    name: '',
    description: '',
    is_default: false
  };
};

onMounted(() => {
  loadTemplates();
});
</script>

<style scoped>
.template-selector-container {
  margin-bottom: 0.75rem;
}

/* ========================================
   DISEÑO COMPACTO HORIZONTAL
======================================== */
.template-compact {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.template-inline-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  flex-wrap: wrap;
}

.header-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.25);
}

.header-badge i {
  font-size: 0.875rem;
}

.inline-select-wrapper {
  position: relative;
  flex: 1;
  min-width: 220px;
}

.inline-select {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
}

.inline-select:hover {
  border-color: #94a3b8;
  background: white;
}

.inline-select:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.inline-select-wrapper i {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 0.75rem;
  pointer-events: none;
}

.inline-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: auto;
  padding-right: 3rem;
  /* Espacio para el botón de cerrar */
}

.btn-compact {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-compact i {
  font-size: 0.875rem;
}

.btn-compact:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.btn-compact:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.btn-compact:not(:disabled):active {
  transform: translateY(0);
}

/* Colores de botones */
.btn-yellow {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
}

.btn-green {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.btn-blue {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
}

.btn-cyan {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
}

.btn-orange {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
}

.btn-pink {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* ========================================
   RESPONSIVE
======================================== */
@media (max-width: 1024px) {
  .btn-compact span {
    display: none;
  }

  .btn-compact {
    padding: 0.5rem;
    min-width: 36px;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .template-inline-header {
    padding: 0.625rem;
    gap: 0.5rem;
  }

  .header-badge {
    font-size: 0.75rem;
    padding: 0.4rem 0.625rem;
  }

  .inline-select-wrapper {
    width: 100%;
    min-width: 100%;
  }

  .inline-actions {
    width: 100%;
    margin-left: 0;
  }

  .btn-compact {
    flex: 1;
    justify-content: center;
  }
}

/* ========================================
   INFO BOX Y FORM ELEMENTS (ESPECÍFICOS DEL CONTENIDO)
======================================== */
/* Info box estilo PatientDataGrid */
.columns-manager-intro {
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1e40af;
  font-size: 0.9375rem;
}

.columns-manager-intro i {
  font-size: 1.25rem;
  color: #3b82f6;
}

/* Form Elements */
.form-group {
  margin-bottom: 1.25rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  color: #1e293b;
  background: white;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.6;
}

.form-checkbox {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  padding: 0.5rem 0;
}

.form-checkbox input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid #cbd5e1;
  accent-color: #ef4444;
}

.form-checkbox span {
  color: #334155;
  font-size: 0.9375rem;
  font-weight: 500;
}

/* Warning styles para modal de confirmación */
.warning-text {
  color: #334155;
  margin-bottom: 1rem;
  font-size: 1rem;
  line-height: 1.6;
}

.warning-list {
  list-style: none;
  padding: 0;
  margin: 1.25rem 0;
  background: #fef2f2;
  border-radius: 10px;
  padding: 1rem;
}

.warning-list li {
  padding: 0.625rem 0;
  font-size: 0.9375rem;
  color: #334155;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.confirm-question {
  margin-top: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  font-size: 1.0625rem;
}

.text-success {
  color: #10b981;
}

.text-danger {
  color: #ef4444;
}

.me-2 {
  margin-right: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .template-selector-header {
    flex-direction: column;
    align-items: stretch;
  }

  .template-selector-title {
    min-width: 100%;
  }

  .template-selector {
    min-width: 100%;
  }

  .template-actions {
    width: 100%;
  }

  .btn-template {
    flex: 1;
    justify-content: center;
  }

  .modal-dialog-centered {
    width: 95%;
    max-width: 95%;
  }

  .modal-content {
    max-width: 100%;
  }
}
</style>
